import { Controller } from '@hotwired/stimulus'

/**
 * BackToTopController
 *
 * Handles the back-to-top button behavior:
 *   - Shows/hides based on scroll position
 *   - Smooth scroll to top on click
 *   - Focus management for accessibility
 */
export default class BackToTopController extends Controller {
  static targets = ['button']
  static values = {
    threshold: { type: Number, default: 3 },
  }

  declare readonly buttonTarget: HTMLElement
  declare readonly hasButtonTarget: boolean
  declare thresholdValue: number

  private showThreshold = 0
  private isVisible = false

  connect(): void {
    this.calculateThreshold()
    this.handleScroll()
    window.addEventListener('scroll', this.handleScroll, { passive: true })
    window.addEventListener('resize', this.calculateThreshold, {
      passive: true,
    })
  }

  disconnect(): void {
    window.removeEventListener('scroll', this.handleScroll)
    window.removeEventListener('resize', this.calculateThreshold)
  }

  private calculateThreshold = (): void => {
    this.showThreshold = window.innerHeight * this.thresholdValue
  }

  private handleScroll = (): void => {
    const shouldShow = window.scrollY >= this.showThreshold

    if (shouldShow && !this.isVisible) {
      this.show()
    } else if (!shouldShow && this.isVisible) {
      this.hide()
    }
  }

  private show(): void {
    if (!this.hasButtonTarget) return

    this.isVisible = true
    this.buttonTarget.classList.remove('opacity-0', 'pointer-events-none')
    this.buttonTarget.classList.add('opacity-100')
    this.buttonTarget.setAttribute('aria-hidden', 'false')
  }

  private hide(): void {
    if (!this.hasButtonTarget) return

    this.isVisible = false
    this.buttonTarget.classList.remove('opacity-100')
    this.buttonTarget.classList.add('opacity-0', 'pointer-events-none')
    this.buttonTarget.setAttribute('aria-hidden', 'true')
  }

  scrollToTop(): void {
    window.scrollTo({
      top: 0,
      behavior: 'smooth',
    })

    this.focusFirstElement()
  }

  private focusFirstElement(): void {
    const focusableElements = document.querySelectorAll<HTMLElement>(
      'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])',
    )

    const filteredElements = Array.from(focusableElements).filter(
      (el) => el.getAttribute('tabindex') !== '-1',
    )

    const firstFocusable = filteredElements[0]
    if (firstFocusable) {
      firstFocusable.focus()
    }
  }
}
